// src/components/layout/Navbar.js
import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import TigerLogo from "../common/TigerLogo";
import {
  IconBasketball, IconBaseball, IconFootball, IconSoccer,
  IconTrack, IconSwimming, IconVolleyball,
  IconUsers, IconShield, IconLayers, IconLogout, IconUser,
  IconMenu, IconX, IconHome,
} from "../common/Icons";

const SPORTS = [
  { name: "Basketball", path: "/sports/basketball", icon: IconBasketball },
  { name: "Baseball", path: "/sports/baseball", icon: IconBaseball },
  { name: "Football", path: "/sports/football", icon: IconFootball },
  { name: "Soccer", path: "/sports/soccer", icon: IconSoccer },
  { name: "Track & Field", path: "/sports/track", icon: IconTrack },
  { name: "Swimming", path: "/sports/swimming", icon: IconSwimming },
  { name: "Volleyball", path: "/sports/volleyball", icon: IconVolleyball },
];

export default function Navbar() {
  const { user, logout, hasPermission } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  const isActive = (path) => location.pathname.startsWith(path);

  const canManage = hasPermission("manage_users");

  return (
    <nav className="sticky top-0 z-40 bg-onyx-950/90 backdrop-blur-md border-b border-onyx-800/50">
      {/* Top accent stripe */}
      <div className="h-1 bg-gradient-to-r from-tiger-600 via-tiger-500 to-tiger-600" />

      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo + Brand */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative">
              <TigerLogo size={40} />
              <div className="absolute inset-0 bg-tiger-500/20 rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            <div className="hidden sm:block">
              <div className="font-display text-lg font-bold text-tiger-400 leading-none tracking-wide">
                EVANDER
              </div>
              <div className="font-display text-xs font-medium text-onyx-400 tracking-widest">
                CAMPUS PSAL
              </div>
            </div>
          </Link>

          {/* Desktop Sport Tabs */}
          <div className="hidden lg:flex items-center gap-1">
            <Link
              to="/"
              className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-display tracking-wide transition-all
                ${location.pathname === "/" ? "bg-tiger-500/15 text-tiger-400" : "text-onyx-400 hover:text-onyx-100 hover:bg-onyx-800/50"}`}
            >
              <IconHome size={16} />
              HOME
            </Link>
            {SPORTS.map((s) => {
              const Icon = s.icon;
              const active = isActive(s.path);
              return (
                <Link
                  key={s.path}
                  to={s.path}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-md text-sm font-display tracking-wide transition-all
                    ${active ? "bg-tiger-500/15 text-tiger-400" : "text-onyx-400 hover:text-onyx-100 hover:bg-onyx-800/50"}`}
                >
                  <Icon size={14} />
                  {s.name.toUpperCase()}
                </Link>
              );
            })}
            <Link
              to="/about"
              className={`px-3 py-2 rounded-md text-sm font-display tracking-wide transition-all
                ${isActive("/about") ? "bg-tiger-500/15 text-tiger-400" : "text-onyx-400 hover:text-onyx-100 hover:bg-onyx-800/50"}`}
            >
              ABOUT US
            </Link>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-2">
            {/* Profile dropdown */}
            {user && (
              <div className="relative">
                <button
                  onClick={() => setProfileOpen(!profileOpen)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-onyx-800/60 hover:bg-onyx-800 border border-onyx-700/50 transition-all"
                >
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-tiger-500 to-tiger-700 flex items-center justify-center">
                    <span className="text-xs font-display font-bold text-white">
                      {user.username?.[0]?.toUpperCase()}
                    </span>
                  </div>
                  <span className="hidden sm:block text-sm font-body text-onyx-200">
                    {user.username}
                  </span>
                  <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-tiger-500/15 text-tiger-400">
                    L{user.level}
                  </span>
                </button>

                {profileOpen && (
                  <div className="absolute right-0 mt-2 w-56 rounded-xl bg-onyx-900 border border-onyx-700/50 shadow-xl shadow-black/30 animate-slide-down overflow-hidden">
                    <div className="p-3 border-b border-onyx-800">
                      <p className="text-sm font-display text-onyx-100">{user.username}</p>
                      <p className="text-xs text-onyx-500 font-mono">Level {user.level} {user.is_level_superuser && "· Superuser"}</p>
                    </div>
                    <div className="p-1">
                      <Link
                        to="/profile"
                        onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-onyx-300 hover:bg-onyx-800 rounded-lg transition-colors"
                      >
                        <IconUser size={16} /> Profile
                      </Link>
                      {canManage && (
                        <>
                          <Link
                            to="/admin/users"
                            onClick={() => setProfileOpen(false)}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-onyx-300 hover:bg-onyx-800 rounded-lg transition-colors"
                          >
                            <IconUsers size={16} /> Manage Users
                          </Link>
                          <Link
                            to="/admin/permissions"
                            onClick={() => setProfileOpen(false)}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-onyx-300 hover:bg-onyx-800 rounded-lg transition-colors"
                          >
                            <IconShield size={16} /> Permissions
                          </Link>
                          <Link
                            to="/admin/groups"
                            onClick={() => setProfileOpen(false)}
                            className="flex items-center gap-2 px-3 py-2 text-sm text-onyx-300 hover:bg-onyx-800 rounded-lg transition-colors"
                          >
                            <IconLayers size={16} /> Groups
                          </Link>
                        </>
                      )}
                      <hr className="my-1 border-onyx-800" />
                      <button
                        onClick={() => { setProfileOpen(false); logout(); }}
                        className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      >
                        <IconLogout size={16} /> Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Mobile hamburger */}
            <button
              className="lg:hidden p-2 text-onyx-400 hover:text-onyx-100"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <IconX size={22} /> : <IconMenu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-onyx-800/50 bg-onyx-950/95 backdrop-blur-md animate-slide-down">
          <div className="max-w-7xl mx-auto px-4 py-3 space-y-1">
            <Link
              to="/" onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-display text-onyx-300 hover:bg-onyx-800"
            >
              <IconHome size={16} /> HOME
            </Link>
            {SPORTS.map((s) => {
              const Icon = s.icon;
              return (
                <Link
                  key={s.path}
                  to={s.path}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-display transition-colors
                    ${isActive(s.path) ? "bg-tiger-500/15 text-tiger-400" : "text-onyx-300 hover:bg-onyx-800"}`}
                >
                  <Icon size={16} /> {s.name.toUpperCase()}
                </Link>
              );
            })}
            <Link
              to="/about" onClick={() => setMobileOpen(false)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-display text-onyx-300 hover:bg-onyx-800"
            >
              ABOUT US
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
